/**
 * Provider Benefits — Public Marketing Page
 * Sprint: PROVIDER-BENEFITS-LANDING-PAGE-01
 *
 * SSOT: Content is served by the canonical site_content CMS
 * (`/api/public/site-content/{key}`). No new CMS framework; reuses
 * the existing site_content_routes pattern with three keys:
 *   - provider_benefits_hero   (1 block: headline / body / CTA)
 *   - provider_benefits_rows   (N blocks: each benefit row)
 *   - provider_benefits_cta    (1 block: final CTA band)
 *
 * Every text/order/active state per block is CMS-editable via the
 * existing admin endpoints. Optional `image_url` and `active` fields
 * on each block are honored. Icons are assigned by frontend (visual
 * concern, not content) — they are not part of the CMS contract.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// PB-I18N-01 — canonical app-wide localiser. Copy fields may be a plain
// string (legacy single-language records) or {en, tr, ...}; this resolves
// both with the standard active-language -> en -> first-non-empty chain.
import { getLocalizedField } from '../../context/LanguageContext';
import Header from '../../components/Header';
import SEO from '../../components/SEO';
// Shared V3 visual refinement — `.v3-root` is the scope hook. Visual only.
import '../landing-v3/landing-v3.css';
import { Button } from '../../components/ui/button';
import {
  Globe2, Inbox, ShieldCheck, Workflow, Wallet, BadgeCheck,
  ArrowRight, CheckCircle2, Loader2, Sparkles
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Default icon rotation used purely as visual decoration when no
// image_url is provided in the CMS payload. Order-stable by index.
const ROW_ICONS = [Globe2, Inbox, ShieldCheck, Workflow, Wallet, BadgeCheck];

async function fetchContent(key) {
  try {
    const r = await fetch(`${API}/api/public/site-content/${key}`);
    if (!r.ok) return null;
    const j = await r.json();
    return j?.content?.payload || null;
  } catch (_e) {
    return null;
  }
}

const Hero = ({ payload }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n?.language?.split('-')[0] || 'en';
  const raw = (payload?.blocks || []).find((b) => b.active !== false) || {};
  const block = {
    ...raw,
    headline: getLocalizedField(raw.headline, lang),
    body_md: getLocalizedField(raw.body_md, lang),
    cta_text: getLocalizedField(raw.cta_text, lang),
  };
  return (
    <section
      data-testid="provider-benefits-hero"
      className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50/40 border-b border-slate-100"
    >
      <div className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 20%, #0f172a 1px, transparent 1px), radial-gradient(circle at 80% 60%, #0f172a 1px, transparent 1px)',
          backgroundSize: '32px 32px, 48px 48px'
        }}
      />
      <div className="max-w-6xl mx-auto px-6 pt-14 pb-12 md:pt-20 md:pb-16 relative">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-100 mb-6">
          <Sparkles className="w-3.5 h-3.5" /> {t('provider.providerBenefits.forProviders')}
        </div>
        <h1
          data-testid="provider-benefits-hero-headline"
          className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 max-w-3xl leading-[1.1]"
        >
          {block.headline || t('provider.providerBenefits.heroFallbackHeadline')}
        </h1>
        <p
          data-testid="provider-benefits-hero-body"
          className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed"
        >
          {block.body_md || t('provider.providerBenefits.heroFallbackBody')}
        </p>
        <div className="mt-10 flex flex-wrap items-center gap-4">
          {block.cta_text && block.cta_url && (
            <Link to={block.cta_url} data-testid="provider-benefits-hero-cta">
              <Button size="lg" className="bg-slate-900 hover:bg-slate-800 text-white px-6 h-12 text-base">
                {block.cta_text}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          )}
          <Link to="/clinic/application-status" data-testid="provider-benefits-hero-secondary">
            <Button size="lg" variant="outline" className="h-12 px-6 text-base border-slate-300">
              {t('provider.providerBenefits.checkApplicationStatus')}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

const BenefitRow = ({ block: raw, index }) => {
  const { i18n } = useTranslation();
  const lang = i18n?.language?.split('-')[0] || 'en';
  // PB-I18N-01 — copy follows the UI language. The image, alt and caption are
  // NOT touched here: the backend surface resolver already decided which
  // jurisdiction's asset this visitor may receive and resolved them together.
  const block = {
    ...raw,
    headline: getLocalizedField(raw.headline, lang),
    body_md: getLocalizedField(raw.body_md, lang),
    cta_text: getLocalizedField(raw.cta_text, lang),
  };
  const Icon = ROW_ICONS[index % ROW_ICONS.length];
  const reverse = index % 2 === 1;
  const imageUrl = block.image_url;
  const imageAlt = block.image_alt || block.headline || '';
  const imageCaption = block.image_caption || '';

  return (
    <div
      data-testid={`provider-benefits-row-${index}`}
      className={`flex flex-col md:flex-row ${reverse ? 'md:flex-row-reverse' : ''} items-center gap-8 md:gap-12 py-10 md:py-12 border-b border-slate-100 last:border-b-0`}
    >
      <div className="flex-1">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-blue-50 text-blue-700 border border-blue-100 mb-5">
          <Icon className="w-6 h-6" />
        </div>
        <h3
          data-testid={`provider-benefits-row-${index}-headline`}
          className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight mb-3"
        >
          {block.headline}
        </h3>
        <p
          data-testid={`provider-benefits-row-${index}-body`}
          className="text-base md:text-lg text-slate-600 leading-relaxed max-w-xl"
        >
          {block.body_md}
        </p>
      </div>
      <div className="flex-1 w-full">
        {imageUrl ? (
          <figure className="mx-auto w-full max-w-md">
            <img
              src={imageUrl}
              alt={imageAlt}
              className="w-full aspect-[4/3] object-cover rounded-2xl shadow-sm border border-slate-100"
              data-testid={`provider-benefits-row-${index}-image`}
            />
            {imageCaption && (
              <figcaption
                className="mt-2 text-xs text-slate-500 text-center"
                data-testid={`provider-benefits-row-${index}-caption`}
              >
                {imageCaption}
              </figcaption>
            )}
          </figure>
        ) : (
          <div
            aria-hidden="true"
            className="w-full max-w-md aspect-[4/3] mx-auto rounded-2xl border border-slate-100 bg-gradient-to-br from-slate-50 to-blue-50/60 flex items-center justify-center"
          >
            <Icon className="w-16 h-16 text-blue-300" />
          </div>
        )}
      </div>
    </div>
  );
};

const FinalCTA = ({ payload }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n?.language?.split('-')[0] || 'en';
  const raw = (payload?.blocks || []).find((b) => b.active !== false) || {};
  const block = {
    ...raw,
    headline: getLocalizedField(raw.headline, lang),
    body_md: getLocalizedField(raw.body_md, lang),
    cta_text: getLocalizedField(raw.cta_text, lang),
  };
  return (
    <section
      data-testid="provider-benefits-final-cta"
      className="bg-slate-900 text-white"
    >
      <div className="max-w-5xl mx-auto px-6 py-14 md:py-16 text-center">
        <h2
          data-testid="provider-benefits-final-cta-headline"
          className="text-3xl md:text-4xl font-semibold tracking-tight"
        >
          {block.headline || t('provider.providerBenefits.ctaFallbackHeadline')}
        </h2>
        <p
          data-testid="provider-benefits-final-cta-body"
          className="mt-4 text-slate-300 text-base md:text-lg max-w-2xl mx-auto leading-relaxed"
        >
          {block.body_md || t('provider.providerBenefits.ctaFallbackBody')}
        </p>
        {block.cta_text && block.cta_url && (
          <div className="mt-10">
            <Link to={block.cta_url} data-testid="provider-benefits-final-cta-button">
              <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 px-8 h-12 text-base">
                {block.cta_text}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

const ShowcaseBlocks = ({ payload }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n?.language?.split('-')[0] || 'en';
  const blocks = (payload?.blocks || [])
    .filter((b) => b.active !== false)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .slice(0, 4)
    .map((b) => ({
      ...b,
      headline: getLocalizedField(b.headline, lang),
      body_md: getLocalizedField(b.body_md, lang),
      cta_text: getLocalizedField(b.cta_text, lang),
    }));
  if (blocks.length === 0) return null;
  return (
    <section className="bg-slate-50 border-t border-slate-100" data-testid="provider-benefits-showcase">
      <div className="max-w-6xl mx-auto px-6 py-12 md:py-16">
        <div className="mb-8 md:mb-10 max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900"
              data-testid="provider-benefits-showcase-heading">
            {t('provider.providerBenefits.showcaseHeading', 'Inside the provider workspace')}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {blocks.map((block, idx) => {
            const Icon = ROW_ICONS[idx % ROW_ICONS.length];
            const img = block.image_url || block.compressed_url || block.thumbnail_url;
            return (
              <article
                key={`${block.headline}-${idx}`}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col shadow-sm transition-shadow hover:shadow-md"
                data-testid={`provider-benefits-showcase-block-${idx}`}
              >
                {img ? (
                  <img
                    src={img}
                    alt={getLocalizedField(block.image_alt, lang) || block.headline || ''}
                    className="w-full aspect-[16/10] object-cover bg-slate-100"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    data-testid={`provider-benefits-showcase-block-${idx}-image`}
                  />
                ) : (
                  <div className="w-full aspect-[16/10] bg-gradient-to-br from-slate-50 to-blue-50/60 flex items-center justify-center"
                       data-testid={`provider-benefits-showcase-block-${idx}-placeholder`}>
                    <Icon className="w-14 h-14 text-blue-300" />
                  </div>
                )}
                <div className="p-6 flex flex-col gap-2 flex-1">
                  <h3 className="text-base md:text-lg font-semibold text-slate-900"
                      data-testid={`provider-benefits-showcase-block-${idx}-headline`}>
                    {block.headline}
                  </h3>
                  {block.body_md && (
                    <p className="text-sm md:text-base text-slate-600 leading-relaxed"
                       data-testid={`provider-benefits-showcase-block-${idx}-body`}>
                      {block.body_md}
                    </p>
                  )}
                  {block.cta_text && block.cta_url && (
                    <div className="mt-auto pt-3">
                      <Link to={block.cta_url} data-testid={`provider-benefits-showcase-block-${idx}-cta`}>
                        <Button variant="outline" size="sm">
                          {block.cta_text}
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const TrustStrip = () => {
  const { t } = useTranslation();
  return (
  <section
    data-testid="provider-benefits-trust-strip"
    className="bg-white border-y border-slate-100"
  >
    <div className="max-w-6xl mx-auto px-6 py-6 md:py-8 grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
      {[
        { label: t('provider.providerBenefits.trustVerifiedLabel'), sub: t('provider.providerBenefits.trustVerifiedSub') },
        { label: t('provider.providerBenefits.trustConsentLabel'), sub: t('provider.providerBenefits.trustConsentSub') },
        { label: t('provider.providerBenefits.trustReachLabel'), sub: t('provider.providerBenefits.trustReachSub') },
        { label: t('provider.providerBenefits.trustWorkspaceLabel'), sub: t('provider.providerBenefits.trustWorkspaceSub') },
      ].map((it) => (
        <div key={it.label} className="px-2">
          <div className="flex items-center justify-center gap-2 text-slate-900 font-medium">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span className="text-sm">{it.label}</span>
          </div>
          <div className="mt-1 text-xs text-slate-500">{it.sub}</div>
        </div>
      ))}
    </div>
  </section>
  );
};

const ProviderBenefits = () => {
  const { t, i18n } = useTranslation();
  const [hero, setHero] = useState(null);
  const [rows, setRows] = useState(null);
  const [cta, setCta] = useState(null);
  const [showcase, setShowcase] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [h, r, c, s] = await Promise.all([
        fetchContent('provider_benefits_hero'),
        fetchContent('provider_benefits_rows'),
        fetchContent('provider_benefits_cta'),
        // TREATX-PROVIDER-SHOWCASE-01 — approval-controlled four blocks. The
        // public endpoint serves the ACTIVE version only, so a staged draft
        // never reaches this page.
        fetchContent('provider_benefits_showcase')
      ]);
      if (!mounted) return;
      setHero(h);
      setRows(r);
      setCta(c);
      setShowcase(s);
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, []);

  // Filter + sort rows by CMS controls (active + sort_order)
  const activeRows = (rows?.blocks || [])
    .filter((b) => b.active !== false)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  // TREATX-SEO-RECOVERY-S1 — this public page emitted no metadata, so it
  // relied on the static shell tags that S1 removes. Title/description come
  // from the SAME canonical CMS hero block the page renders (no second
  // source, no new copy), with the existing i18n fallbacks.
  const lang = i18n?.language?.split('-')[0] || 'en';
  const heroBlock = (hero?.blocks || []).find((b) => b.active !== false) || {};
  const seoTitle = getLocalizedField(heroBlock.headline, lang)
    || t('provider.providerBenefits.heroFallbackHeadline');
  const seoDescription = getLocalizedField(heroBlock.body_md, lang)
    || t('provider.providerBenefits.heroFallbackBody');

  return (
    <div className="v3-root min-h-screen bg-white" data-testid="provider-benefits-page">
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonicalPath="/provider-benefits"
        lang={lang}
        locale={lang === 'tr' ? 'tr_TR' : 'en_US'}
      />
      <Header />

      {loading ? (
        <div className="min-h-[60vh] flex items-center justify-center" data-testid="provider-benefits-loading">
          <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
        </div>
      ) : (
        <>
          <Hero payload={hero} />
          <TrustStrip />

          <section
            data-testid="provider-benefits-rows"
            className="bg-white"
          >
            <div className="max-w-6xl mx-auto px-6 py-12 md:py-16">
              <div className="mb-10 max-w-2xl">
                <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
                  {t('provider.providerBenefits.whatYouGet')}
                </h2>
                <p className="mt-3 text-slate-600 text-base md:text-lg">
                  {t('provider.providerBenefits.builtAround')}
                </p>
              </div>
              {activeRows.length === 0 ? (
                <div className="text-sm text-slate-500 py-10 text-center" data-testid="provider-benefits-rows-empty">
                  {t('provider.providerBenefits.noBenefits')}
                </div>
              ) : (
                <div>
                  {activeRows.map((block, idx) => (
                    <BenefitRow key={`${block.headline}-${idx}`} block={block} index={idx} />
                  ))}
                </div>
              )}
            </div>
          </section>

          <ShowcaseBlocks payload={showcase} />

          <FinalCTA payload={cta} />
        </>
      )}
    </div>
  );
};

export default ProviderBenefits;
